import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useOpenWindow } from '@krp/client-runtime'
import {
  CASHIER_GROUPS_WINDOWS, CashierReportScreen, CashierShiftsTab, limitShiftRange, receiptsGroupPage, receiptsGroupSummary, receiptsGroups,
  receiptsReportShifts, type CashierReportTab,
} from '@krp/resource-receipts-ui'
import { firstOfMonth, lastOfMonth } from '@krp/ui-kit'
import { errorText, useOriginal, useReportRange } from './reportHooks'

/** Вкладка статистики в адресе (`?ctab=shifts`); «Продажи» — по умолчанию, без параметра. */
const TAB_PARAM = 'ctab'

/**
 * Статистика кассира нашей системы (RP-CU9): администратор выбирает нашего кассира из списка и видит сводную статистику по всем его связям —
 * без кода кассира (доступ по праву аналитики, решение владельца 02.11.2026). Источник и кассир источника больше не выбираются: один кассир,
 * одна статистика по всем источникам.
 */
export function CashierStatisticsBinding() {
  const open = useOpenWindow()
  const { intent, today, from, to, patch } = useReportRange()
  // Что открыто — в адресе: вкладка «Продажи» | «Смены» переживает обновление страницы и не копит шаги истории.
  const [search, setSearch] = useSearchParams()
  const tab: CashierReportTab = search.get(TAB_PARAM) === 'shifts' ? 'shifts' : 'sales'
  const [personId, setPersonId] = useState('')
  const directory = receiptsGroups.useQuery()
  // Кассиры нашей системы (не в архиве): выбор — по человеку, а не по источнику. Статистика суммирует все его связи.
  const people = (directory.currentData?.people ?? []).filter((person) => !person.archived)
  const person = people.find((item) => item.id === personId)
  const month = firstOfMonth(from)
  // Порядок чтения: сначала выбранный день (по умолчанию — сегодня), потом весь месяц для графика. Вкладке «Смены» нужен весь месяц сразу.
  const singleDay = from === to && tab === 'sales'
  const groupArgs = { id: person?.id ?? '', month, timeZoneId: intent.timeZoneId, refresh: intent.refresh }
  const monthState = receiptsGroupSummary.useQueryState(groupArgs, { skip: person === undefined })
  const dayGroup = receiptsGroupSummary.useQuery({ ...groupArgs, from, to }, { skip: person === undefined || !singleDay || monthState.isSuccess })
  const dayDone = !singleDay || dayGroup.isSuccess || dayGroup.isError || monthState.isSuccess
  const groupSummary = receiptsGroupSummary.useQuery(groupArgs, { skip: person === undefined || !dayDone })
  const monthData = groupSummary.isError ? undefined : groupSummary.currentData
  const dayData = dayGroup.currentData
  // Месяц пришёл — итоги и график из него; пока его нет — из дня. Чеки и смены читаются по снимку того отчёта, что на экране.
  const data = person === undefined ? undefined : monthData ?? dayData
  const monthPending = person !== undefined && !groupSummary.isSuccess && !groupSummary.isError
  const busy = directory.isFetching || dayGroup.isFetching || (data === undefined && groupSummary.isFetching)
  const trendBusy = groupSummary.isFetching || monthPending
  const pageArgs = { id: person?.id ?? '', snapshotId: data?.snapshotId ?? '', from, to, place: intent.place, kind: intent.kind, search: intent.search, offset: intent.offset }
  const page = receiptsGroupPage.useQuery(pageArgs, { skip: data === undefined || tab === 'shifts' })
  const original = useOriginal()
  // Смены — по снимку месячного отчёта, который на экране: смены по чекам кассира из всех связанных источников.
  const shiftSnapshot = tab === 'shifts' && person !== undefined ? monthData?.snapshotId ?? '' : ''
  const shifts = receiptsReportShifts.useQuery(shiftSnapshot, { skip: shiftSnapshot === '' })
  const chooseTab = (next: CashierReportTab) => {
    setSearch((current) => { const value = new URLSearchParams(current); if (next === 'shifts') value.set(TAB_PARAM, next); else value.delete(TAB_PARAM); return value }, { replace: true })
    // «Смены» показывают месяц целиком: выбранный день продаж раскрывается до своего месяца.
    if (next === 'shifts') patch({ from: month, to: lastOfMonth(month) })
  }
  const cashierOptions = [{ id: '', label: 'Выберите кассира' }, ...people.map((item) => ({ id: item.id, label: item.name }))]
  const error = errorText(directory.error ?? groupSummary.error ?? (data === undefined ? dayGroup.error : undefined) ?? page.error ?? shifts.error) ?? original.error
  return <CashierReportScreen personId={personId} range={{ from, to }} today={today} timeZoneId={intent.timeZoneId} place={intent.place} kind={intent.kind} search={intent.search}
    directory={directory.currentData} cashierOptions={cashierOptions}
    summary={data} page={data === undefined || page.isError ? undefined : page.currentData} offset={intent.offset} busy={busy} trendBusy={trendBusy} pageBusy={page.isFetching}
    dailyTable receiptsOpen={false}
    error={error}
    patch={(value) => { if (value.personId !== undefined) { setPersonId(value.personId); patch({ place: '', offset: 0 }); return } patch(value) }}
    reload={() => patch({ refresh: intent.refresh + 1 })} links={() => open(CASHIER_GROUPS_WINDOWS.report)} original={original.original}
    tab={tab} onTab={chooseTab}
    shiftsContent={<CashierShiftsTab today={today} range={{ from, to }} timeZoneId={intent.timeZoneId} place={intent.place} data={shiftSnapshot === '' ? undefined : shifts.currentData}
      busy={shifts.isFetching} pending={monthPending} onPeriod={(range) => patch(range)} onMonth={(value) => patch({ from: value, to: lastOfMonth(value) })}
      sales={(range) => { const days = limitShiftRange(range, from); if (days !== null) { patch(days); chooseTab('sales') } }} />} />
}
